/**
 * Press Quotes Redesign - Glide.js Carousel
 * Initializes the press quotes carousel with responsive breakpoints
 */

class PressQuotesCarousel {
  constructor(selector) {
    this.selector = selector;
    this.glide = null;
    this.init();
  }

  init() {
    // Check if Glide.js is loaded
    if (typeof Glide === 'undefined') {
      console.error('Glide.js is not loaded. Please ensure the library is included.');
      return;
    }

    // Check if element exists
    const element = document.querySelector(this.selector);
    if (!element) {
      console.warn(`Press quotes carousel element not found: ${this.selector}`);
      return;
    }

    this.initializeGlide();
  }

  initializeGlide() {
    // Get autoplay setting from data attribute
    const element = document.querySelector(this.selector);
    const autoplayEnabled = element.dataset.autoplay === 'true';
    
    const config = {
      type: 'carousel',
      startAt: 0,
      perView: 5,
      gap: 48,
      peek: 0,
      hoverpause: true,
      animationDuration: 400,
      animationTimingFunc: 'ease',
      breakpoints: {
        1024: {
          perView: 4,
          gap: 20,
          peek: 0
        },
        768: {
          perView: 2,
          gap: 20,
          peek: 0
        },
        480: {
          perView: 1,
          gap: 16,
          peek: {
            before: 120,
            after: 120
          }
        }
      }
    };

    // Only add autoplay if enabled in settings
    if (autoplayEnabled) {
      config.autoplay = 5000;
    }

    try {
      this.glide = new Glide(this.selector, config);
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Mount the carousel
      this.glide.mount();
      
      // Fallback: ensure images are shown after a maximum delay
      setTimeout(() => {
        const element = document.querySelector(this.selector);
        if (element) {
          const images = element.querySelectorAll('.press_quote img');
          images.forEach(img => {
            img.style.opacity = '1';
          });
        }
      }, 1000);
      
      // console.log('Press quotes carousel initialized successfully');
    } catch (error) {
      console.error('Error initializing press quotes carousel:', error);
    }
  }

  setupEventListeners() {
    if (!this.glide) return;

    // Handle initialization complete
    this.glide.on('build.after', () => {
      const element = document.querySelector(this.selector);
      if (element) {
        element.classList.remove('glide-not-init');
        element.classList.add('glide-initialized');
        
        // Show images after a brief delay to ensure smooth transition
        setTimeout(() => {
          const images = element.querySelectorAll('.press_quote img');
          images.forEach(img => {
            img.style.opacity = '1';
          });
        }, 100);
      }
    });

    // Handle slide changes
    this.glide.on('run.after', (move) => {
      // Custom logic for slide changes can be added here
      this.onSlideChange(move);
    });

    // Pause autoplay on focus (accessibility)
    this.glide.on('mount.after', () => {
      const slides = document.querySelectorAll(`${this.selector} .glide__slide`);
      slides.forEach(slide => {
        slide.addEventListener('focus', () => this.pauseAutoplay());
        slide.addEventListener('blur', () => this.resumeAutoplay());
      });
    });
  }

  onSlideChange(move) {
    // Trigger custom event for external listeners
    const event = new CustomEvent('pressQuotesSlideChange', {
      detail: { 
        direction: move.direction,
        index: this.glide.index 
      }
    });
    document.dispatchEvent(event);
  }

  pauseAutoplay() {
    if (this.glide && this.glide.settings.autoplay) {
      this.glide.pause();
    }
  }

  resumeAutoplay() {
    if (this.glide && this.glide.settings.autoplay) {
      this.glide.play();
    }
  }

  destroy() {
    if (this.glide) {
      this.glide.destroy();
      this.glide = null;
    }
  }

  // Public API methods
  goTo(index) {
    if (this.glide) {
      this.glide.go(`=${index}`);
    }
  }

  next() {
    if (this.glide) {
      this.glide.go('>');
    }
  }

  previous() {
    if (this.glide) {
      this.glide.go('<');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Find all press quotes carousels and initialize them
  const carousels = document.querySelectorAll('.glide-press-quotes');
  
  carousels.forEach((carousel, index) => {
    const sectionId = carousel.closest('[id^="shopify-section-"]')?.id;
    if (sectionId) {
      const selector = `#${sectionId} .glide-press-quotes`;
      new PressQuotesCarousel(selector);
    }
  });
});

// Global function for manual initialization (if needed)
window.initPressQuotesCarousel = function(selector) {
  return new PressQuotesCarousel(selector);
};
